import { AppDate } from "../envTypes";

export enum CommentSoldEnv {
  lima = "lima",
  hotel = "hotel",
  qaext = "qaext",
}

export enum CommentSoldShopStatus {
  Pending = "PENDING",
  Created = "CREATED",
  Failed = "FAILED",
}

export enum CreatorRegType {
  Individual = "individual",
  Business = "business",
}

export interface ReadonlyProfile {
  noCommissionBalance?: number;
  /**
   * @deprecated replaced by `withdrawableBalance`
   */
  balance?: number;
  /**
   * @deprecated replaced by `withdrawablePendingBalance`
   */
  pendingBalance?: number;
  bonusBalance?: number;
  referralBalance?: number;
  referralCount?: number;
  totalReferral?: number;
  newClientBonus?: boolean;
  commissionRate?: number;
  gatewayProcessingFeeRate?: number; // 2.9% per order from 09/01/23
  gatewayProcessingFeeExtra?: number; // $0.3 per capture group from 09/01/23
  // if shippingFee is nil, it is using goshippo.
  // if shippingFee is not nil, it is using custom shipping
  shippingFee?: number;
  // if this is set, it overrides the global shipping fee setting
  adjustedPopshopShippingFee?: number;
  /**
   * The price a buyer pays when shipping with shippo platform.
   *
   * Currently, it could be one of $2.99, $5.99, $9.99, and $15.99
   */
  shippingPrice?: number;
  // can only be true when features.shippoPlatform is enabled
  arrangeMyOwnShippingV2?: boolean;
  userGroupIds?: string[];
  withdrawableBalance?: number;
  withdrawablePendingBalance?: number;
  tokenBalance?: number;
  isLiveStreamStartNotificationDisabled?: boolean;
  features?: {
    commentchat?: boolean;
    commentchatOtherLinkType?: boolean;
    isCommentchatOnboardingOptional?: boolean;
    modelMe?: boolean;
    pickup?: boolean;
    unlimitedStoreFavorites?: boolean;
    shopifyOrderSync?: boolean;
    // set order number format as PS-#{PSL order number}
    shopifyOrderSyncWithPSPrefix?: boolean;
    shopifyWriteDraftOrders?: boolean;
    shopifyListingSyncWithoutSKU?: boolean;
    nftGatedShows?: boolean;
    patreonGatedShows?: boolean;
    // Allow additional Audience data in SD
    customersTab?: boolean;
    // if false, user can't schedule upcoming show or start livestream
    streaming?: boolean;
    voidLabel?: boolean;
    customBadge?: boolean;
    shippoPlatform?: boolean;
    tipping?: boolean;
    captureGroup?: boolean;

    stripeConnect?: boolean;
    commentSoldAccountSync?: boolean;
    // depends on commentSoldAccountSync
    commentSoldProductSync?: boolean;
    // depends on commentSoldProductSync
    commentSoldOrderSync?: boolean;
    commentSoldTax?: boolean;
    /**
     * whether to display dropship UI
     *
     * for creators, this is always true; for other types of sellers, this is false by default unless otherwise manually enabled.
     *
     * note: this feature toggle is only for UI display, it will not affect backend logic.
     */
    dropshipUI?: boolean;

    cloudRecording?: boolean;
    preferReplaysHiddenInApp?: boolean;
    /**
     * default is true
     */
    storeReelPopupOnProfile?: boolean;
    /**
     * default is true
     */
    storeReplayPopupOnProfile?: boolean;
    /**
     * If PSL Sourced (from PSL Catalog) badge should be display where applicable
     */
    showPslSourcedBadge?: boolean;
    // if === false, stores cannot sell products
    orders?: boolean;
    // if === false, do not display storefront widget for this store
    storefrontUI?: boolean;

    // if === false, do not display save theme for this store
    enableThemeCreator?: boolean;
    /**
     * For the US pop store creator: if true, use on_behalf_of for the stripe payment, and enable the card_payment for Stripe connect account
     * For the non-US pop store creator, it alway enable on_behalf_of as default, can not change by this value
     */
    onBehalfOfChargeModeEnabled?: boolean;
    /**
     * Indicates whether the onboarded user is using the new CommentChat Meta onboarding flow.
     * - `true`: User is onboarded with the Meta flow.
     * - `false` or `undefined`: User is onboarded with the legacy PopshopLive flow.
     */
    isCommentChatOnboardingWithMetaEnabled?: boolean;
    /**
     * Indicates whether clip interactions are hidden for the store.
     * - `true`: Clip interactions (e.g., likes, comments) are hidden from viewers.
     * - `false` or `undefined`: Clip interactions are visible and enabled.
     */
    isClipInteractionsHidden?: boolean;
    isAvatarMeEnabled?: boolean;
    isAnalyticsEnabled?: boolean;
    /**
     * @deprecated on 2026-01-06
     */
    isPromoPushEnabled?: boolean;
    /**
     * @deprecated on 2026-01-06
     */
    isAlterEgoEnabled?: boolean;
    isSponsoredCreator?: boolean;
    /**
     * Set with `isSponsoredCreator` when the sponsorship came from signing up
     * through an agency's link: the agency pays. Such a creator got the
     * agency's one-time `signupBonusTokens` and is NOT refilled by
     * `updateSponsoredCreatorBonusToken`, which tops up admin-sponsored
     * creators only.
     */
    sponsoredByAgencyId?: string;
    /**
     * The creator belongs to an agency, which pays for their plan: every plan
     * gate passes, they spend credits (no free AI, no top-ups).
     *
     * Written ONLY by the agency tag — set when they sign up through an
     * agency's link or are assigned to one, cleared when they leave it
     * (untagged, removed from the portal, account deleted). Never a manual
     * switch: it is a fact about the tag, so nothing else may set it.
     */
    agencyCreator?: AgencyCreatorFeature;
    /**
     * Controls visibility of the "Meet the concierge team" banner on the
     * Real Estate storefront.  `false` hides the banner;
     * `undefined` / `true` shows it. Toggled per-seller from the admin user info page.
     */
    isMeetRealEstateConciergeTeamBannerEnabled?: boolean;
    /**
     * Shows the real-estate event sign-in QR / free-guide banner on the
     * real-estate home. Off unless `true`. Toggled per-user from the admin
     * user info page.
     */
    isRealEstateEventBannerEnabled?: boolean;
    isAlterEgoAdvancedEnabled?: boolean;
    isSetupPriorityInboxEnabled?: boolean;
    isNewCustomizationExperienceEnabled?: boolean;
    isAIChiefEnabled?: boolean;
    isAIEchoEnabled?: boolean;
    isAdvancedStylingEnabled?: boolean;
    /** Whether the seller has enabled the Gmail integration for Echo-Me. */
    isGmailForEchoMeEnabled?: boolean;
    /** Whether the seller has enabled the TikTok integration for Echo-Me. */
    isTiktokForEchoMeEnabled?: boolean;
    isNewMetaConnectionEnabled?: boolean;
    isBetaFeaturesEnabled?: boolean;
    isGrowthRxEnabled?: boolean;
    /**
     * Follower Ranking's "Rank your followers" card — ranking the followers who
     * commented on past posts. Off unless `true`. Toggled per-user from the
     * admin store permissions page ("Show Past Follower Ranking").
     */
    isPastFollowerRankingEnabled?: boolean;
    /**
     * Growth Rx AUTO-POST — "Post now" / "Schedule" to Instagram on the Studio
     * card. Off unless `true`; a per-account rollout gate on top of Meta's own
     * permission check. Toggled per-user from the admin store permissions page
     * ("Growth Rx Instagram Auto-post").
     */
    isInstagramAutoPostEnabled?: boolean;
  };
  // when isDormantSeller turns true, features.streaming turns false and seller dashboard access is blocked. vice versa.
  isDormantSeller?: boolean;
  // history records for isDormantSeller operations, and kept lastest 4 records.
  noteDormantSeller?: string[];
  nftContracts?: string[];
  patreonIdentifier?: string;
  taxIDConfirmed?: boolean;
  notes?: string;
  customBadgeLimit?: number;
  stripeCustomerId?: string;
  shippo?: {
    merchantId: string;
    uspsMasterCarrierId: string;
  };
  commentSoldShop?: {
    id?: string;
    status?: CommentSoldShopStatus;
    createdAt?: AppDate;
    failedAt?: AppDate;
    failureReason?: string;
    // expired after 24 hours, will be recreated after 12 hours using a cronjob
    token?: string;
    tokenUpdatedAt?: AppDate;
    webhook?: {
      success: boolean;
      // e.g. https://api.d.popshop.live
      baseUrl: string;
      errors: string[];
    };
    /**
     * The flag of CommentSold env for PSL in DEV
     */
    commentSoldEnv?: CommentSoldEnv;
  };
  sellerCategoryConfirmed?: boolean;
  /**
   * PSLPay balance migration
   */
  pslPayBalanceMigration?: {
    /** amount for create withdraw transaction of psl available balance */
    withdrawAmount?: number;
    /** amount for transfer balance to stripe custom account from psl pending balance */
    stripeTransferAmount?: number;
    updateAt: AppDate;
  };
  popStorePay?: {
    // delay time (s) for withdraw after successful stripe account setup
    withdrawDelayAfterAccountSetupInSeconds?: number;
    // delay time (s) for withdraw after add/edit payout method
    withdrawDelayAfterUpsertPaymentMethodInSeconds?: number;
    // delay time (s) for balance available after subscribe
    creatorSubscriptionBalanceAvailableDelayInSeconds?: number;
    // delay time (s) for digital download balance available after order create
    digitalDownloadBalanceAvailableDelayInSeconds?: number;
    // delay time (s) for meeting slot balance available after order create
    meetingSlotBalanceAvailableDelayInSeconds?: number;
    // delay time (s) for derived product balance available after order create
    derivedProductBalanceAvailableDelayInSeconds?: number;
  };
  enableStripePaymentElement?: boolean;
  creatorRegType?: CreatorRegType;
  generatedAvatarMeFreeImages?: boolean;
}

/** `features.agencyCreator`: which agency, and since when (epoch ms). */
export interface AgencyCreatorFeature {
  agencyId: string;
  since: number;
}

type CreatorFeatures = Pick<
  NonNullable<ReadonlyProfile['features']>,
  'isSponsoredCreator' | 'agencyCreator'
>;

/** The agency this creator belongs to (`agencyCreator`); null when none. */
export function agencyIdOfCreator(features: CreatorFeatures | null | undefined): string | null {
  return features?.agencyCreator?.agencyId || null;
}

/** Every plan gate passes: sponsored by POP.STORE, or covered by an agency. */
export function hasCoveredPlan(features: CreatorFeatures | null | undefined): boolean {
  return features?.isSponsoredCreator === true || agencyIdOfCreator(features) !== null;
}
